import {
  EmployeeSkillGap_EmployeeSkillFragment,
  EmployeeSkillGap_PositionSkillAssociationFragment,
} from '../../__generated__/graphql';
import { isSkillLevel, SkillLevel } from '../../pages/Skills/common/types';

// How the employee's current level compares to what the position requires.
// `missing` is a required skill the employee has no rating for; `none` is a
// requirement with no target level defined, so there is nothing to compare.
export type SkillGapStatus = 'met' | 'below' | 'missing' | 'none';

export type SkillGapRow = {
  id: string;
  skillName: string;
  targetLevel: SkillLevel | undefined;
  currentLevel: SkillLevel | undefined;
  status: SkillGapStatus;
};

export const getSkillGapStatus = (
  currentLevel: SkillLevel | undefined,
  targetLevel: SkillLevel | undefined,
): SkillGapStatus => {
  if (targetLevel == null) {
    return 'none';
  }
  // `null`/undefined current level means the employee is not rated on a skill
  // the position requires — a real gap, not a zero.
  if (currentLevel == null) {
    return 'missing';
  }
  return currentLevel >= targetLevel ? 'met' : 'below';
};

// Target levels come from Salesforce as a plain Int, so anything outside the
// 1-5 skill scale is mapped to `undefined` and rendered as "not defined"
// instead of being shown as a bogus level. `null` is excluded explicitly:
// `isSkillLevel` accepts it (an employee's rating can legitimately be "not
// rated"), but a requirement without a target level has none defined at all.
const toSkillLevel = (value: number | null): SkillLevel | undefined =>
  value !== null && isSkillLevel(value) ? value : undefined;

export const mapSkillGapRows = (
  requirements: readonly EmployeeSkillGap_PositionSkillAssociationFragment[],
  employeeSkills: readonly EmployeeSkillGap_EmployeeSkillFragment[] = [],
): SkillGapRow[] => {
  // The employee's own rating for a skill lives on a separate association, so
  // index it by skill id to pair each requirement with its current level.
  const currentLevelBySkillId = new Map(
    employeeSkills.map((employeeSkill) => [
      employeeSkill.skill.Id,
      toSkillLevel(employeeSkill.flair__Rate__c),
    ]),
  );

  return requirements.map((requirement) => {
    const targetLevel = toSkillLevel(requirement.flair__Rate__c);
    // Undefined covers both "skill not assigned" and "assigned but unrated";
    // both render as "not defined" rather than implying a level of 0.
    const currentLevel = currentLevelBySkillId.get(requirement.skill.Id);

    return {
      id: requirement.Id,
      skillName: requirement.skill.localizedName,
      targetLevel,
      currentLevel,
      status: getSkillGapStatus(currentLevel, targetLevel),
    };
  });
};

// Highest target level first so the most demanding requirements lead, then by
// name for a stable order among equal levels.
export const sortSkillGapRows = (rows: readonly SkillGapRow[]): SkillGapRow[] =>
  [...rows].sort((a, b) => {
    const levelDiff = (b.targetLevel ?? 0) - (a.targetLevel ?? 0);
    return levelDiff !== 0 ? levelDiff : a.skillName.localeCompare(b.skillName);
  });
